/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { exportAllSettings, importAllSettings, SettingEntry } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { useTheme } from 'styled-components';

function AdvancedSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const [allSettings, setAllSettings] = useState<any>({});
    const [originalSettings, setOriginalSettings] = useState<string>('');
    const [refresh, setRefresh] = useState<number>(0);
    const metadataContext = useContext<any>(MetadataContext);
    const settingsContext = useContext(SettingsContext);

    const dirty = JSON.stringify(allSettings) !== originalSettings;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const alls = await exportAllSettings();
                let o: any = {};
                alls.forEach((ent: SettingEntry) => {
                    o[ent.key] = ent;
                });
                setAllSettings(o);
                setOriginalSettings(JSON.stringify(o));
            } catch (err: any) {
                console.error('Error loading advanced settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        await importAllSettings(Object.values(allSettings));
        metadataContext.refresh();
        setRefresh(r => r + 1);
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Advanced Settings</h3>
                    <div className="text-muted">Direct JSON editor for all persisted settings entries.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            try {
                                setAllSettings(JSON.parse(originalSettings || '{}'));
                            } catch {
                                setAllSettings({});
                            }
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>
            <JsonEditor
                data={allSettings}
                collapse={1}
                rootName="settings"
                rootFontSize="13px"
                theme={githubDarkTheme}
                onUpdate={({ newData }) => {
                    setAllSettings(newData);
                }}
            />
        </div>
    );
}

export default AdvancedSettings;
